// src/pages/KarigarManagement/Karigar/KarigarListPage.jsx

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useKarigarManagement } from "@/hooks/useKarigar";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import { Badge } from "@/components/ui-v2/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui-v2/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui-v2/dropdown-menu";
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Calendar,
  Trash2,
  Filter,
  RefreshCw,
} from "lucide-react";
import Pagination from "@/components/common/Pagination";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { debounce } from "lodash";

// Status badge colors
const STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
  suspended: "bg-red-100 text-red-800",
  blacklisted: "bg-red-200 text-red-900",
};

const KarigarListPage = () => {
  const navigate = useNavigate();
  const {
    karigars,
    pagination,
    filters,
    loading,
    error,
    successMessage,
    getKarigars,
    updateFilters,
    clearFilters,
    clearError,
    clearSuccessMessage,
  } = useKarigarManagement();

  const { branches } = useSelector((state) => state.branch);
  const [searchInput, setSearchInput] = useState(filters.search || "");

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      updateFilters({ search: value });
    }, 500),
    []
  );

  // Fetch karigars on mount and filter change
  useEffect(() => {
    getKarigars({
      page: pagination.page,
      limit: pagination.limit,
      ...filters,
    });
  }, [filters, pagination.page]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Handle filter changes
  const handleStatusFilter = (value) => {
    updateFilters({ status: value === "all" ? "" : value });
  };

  const handleBranchFilter = (value) => {
    updateFilters({ branchCode: value === "all" ? "" : value });
  };

  // Handle page change
  const handlePageChange = (page) => {
    getKarigars({
      page,
      limit: pagination.limit,
      ...filters,
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchInput("");
    clearFilters();
  };

  // Navigate to karigar detail
  const handleViewKarigar = (karigarId) => {
    navigate(`/karigar-management/karigars/${karigarId}`);
  };

  // Navigate to edit
  const handleEditKarigar = (karigarId) => {
    navigate(`/karigar-management/karigars/${karigarId}/edit`);
  };

  // Navigate to availability
  const handleViewAvailability = (karigarId) => {
    navigate(`/karigar-management/karigars/${karigarId}/availability`);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Karigar Management</h1>
          <p className="text-muted-foreground">
            Manage your artisans and craftsmen
          </p>
        </div>
        <Button onClick={() => navigate("/karigar-management/karigars/create")}>
          <Plus className="h-4 w-4 mr-2" />
          Add Karigar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, phone..."
                value={searchInput}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={filters.status || "all"}
              onValueChange={handleStatusFilter}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>

            {/* Branch Filter */}
            <Select
              value={filters.branchCode || "all"}
              onValueChange={handleBranchFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches?.map((branch) => (
                  <SelectItem key={branch.code} value={branch.code}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Reset Filters */}
            <Button variant="outline" onClick={handleResetFilters}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading.list ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : karigars.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <p>No karigars found</p>
              <Button
                variant="link"
                onClick={() => navigate("/karigar-management/karigars/create")}
              >
                Add your first karigar
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Karigar</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {karigars.map((karigar) => (
                  <TableRow key={karigar._id}>
                    {/* Karigar Info */}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium">
                            {karigar.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{karigar.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {karigar.karigarCode}
                          </p>
                        </div>
                      </div>
                    </TableCell>

                    {/* Contact */}
                    <TableCell>
                      <p>{karigar.phoneNumber}</p>
                      {karigar.email && (
                        <p className="text-sm text-muted-foreground">
                          {karigar.email}
                        </p>
                      )}
                    </TableCell>

                    {/* Skills */}
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {karigar.skills?.slice(0, 2).map((skill, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="text-xs"
                          >
                            {skill.category?.replace(/_/g, " ")}
                          </Badge>
                        ))}
                        {karigar.skills?.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{karigar.skills.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>

                    {/* Branch */}
                    <TableCell>
                      {karigar.associatedBranches?.find((b) => b.isPrimary)
                        ?.branchName || "-"}
                    </TableCell>

                    {/* Performance */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          ⭐{" "}
                          {karigar.performance?.overallRating?.toFixed(1) ||
                            "0.0"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({karigar.performance?.totalJobsCompleted || 0} jobs)
                        </span>
                      </div>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Badge className={STATUS_COLORS[karigar.status] || ""}>
                        {karigar.status}
                      </Badge>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleViewKarigar(karigar._id)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleEditKarigar(karigar._id)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleViewAvailability(karigar._id)}
                          >
                            <Calendar className="h-4 w-4 mr-2" />
                            View Availability
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default KarigarListPage;
